import React, { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '../firebase';
import { apiFetch } from '../utils/api';
import type { Bot, Analytics } from '../utils/botHelpers';

// Shared per-bot data, provided once at the BotLayout level (one per :botId)
// and read by whichever tab routes actually need it (today: Overview and
// Conversations both read analytics/contacts — see the Dashboard-split plan
// for why this lives here instead of being fetched independently per route).
//
// releasingRef/releasedSenders are agent-handoff race-condition guards that
// used to live as plain refs on the old monolithic Dashboard component,
// which never unmounted on tab switch. Now that Conversations is a route
// that mounts/unmounts on navigation, these two specifically must live here
// (one level up, reset only when botId changes) rather than as local refs
// inside the Conversations route, or a release followed by a quick tab
// bounce within the ~10s guard window would silently reopen the race.
interface BotWorkspaceContextValue {
  bot: Bot | null;
  notFound: boolean;
  loading: boolean;
  analytics: Analytics | null;
  analyticsLoading: boolean;
  contacts: Record<string, any>;
  refreshAnalytics: (silent?: boolean) => Promise<void>;
  refreshContacts: () => Promise<void>;
  refreshBot: () => Promise<void>;
  releasingRef: React.MutableRefObject<boolean>;
  releasedSenders: React.MutableRefObject<Set<string>>;
}

const BotWorkspaceContext = createContext<BotWorkspaceContextValue | null>(null);

export function BotWorkspaceProvider({ botId, children }: { botId: string; children: ReactNode }) {
  const [bot, setBot] = useState<Bot | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [contacts, setContacts] = useState<Record<string, any>>({});

  const releasingRef = useRef(false);
  const releasedSenders = useRef<Set<string>>(new Set());

  const refreshAnalytics = useCallback(async (silent = false) => {
    if (!silent) setAnalyticsLoading(true);
    try {
      const res = await apiFetch(`/api/analytics/${botId}`);
      const data = await res.json();
      if (data.success) setAnalytics(data.data);
    } catch (e) {
      console.error('Analytics error:', e);
    } finally {
      if (!silent) setAnalyticsLoading(false);
    }
  }, [botId]);

  const refreshContacts = useCallback(async () => {
    try {
      const snap = await get(ref(db, `contacts/${botId}`));
      if (snap.exists()) {
        const data: Record<string, any> = {};
        snap.forEach(c => { data[c.key!] = c.val(); });
        setContacts(data);
      } else {
        setContacts({});
      }
    } catch (e) { /* silent */ }
  }, [botId]);

  // Re-fetches the bot record itself — used after Settings saves so the
  // workspace header/tabs immediately reflect businessName/status/etc.
  // changes without waiting for a full botId-change remount.
  const refreshBot = useCallback(async () => {
    try {
      const snap = await get(ref(db, `bots/${botId}`));
      if (snap.exists()) setBot({ id: botId, ...snap.val() });
    } catch (e) { /* silent */ }
  }, [botId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    setBot(null);
    setAnalytics(null);
    setAnalyticsLoading(true);
    setContacts({});
    // Bot-switch boundary: these guards must not leak across different bots,
    // only survive tab bounces within the same one.
    releasingRef.current = false;
    releasedSenders.current = new Set();

    (async () => {
      try {
        const snap = await get(ref(db, `bots/${botId}`));
        if (!alive) return;
        if (!snap.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setBot({ id: botId, ...snap.val() });
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        console.error('Bot load error:', e);
        setNotFound(true);
        setLoading(false);
      }
    })();

    refreshAnalytics();
    refreshContacts();

    return () => { alive = false; };
  }, [botId, refreshAnalytics, refreshContacts]);

  const value: BotWorkspaceContextValue = {
    bot, notFound, loading, analytics, analyticsLoading, contacts,
    refreshAnalytics, refreshContacts, refreshBot, releasingRef, releasedSenders,
  };

  return <BotWorkspaceContext.Provider value={value}>{children}</BotWorkspaceContext.Provider>;
}

export function useBotWorkspace(): BotWorkspaceContextValue {
  const ctx = useContext(BotWorkspaceContext);
  if (!ctx) throw new Error('useBotWorkspace must be used within a BotWorkspaceProvider');
  return ctx;
}
